import React, { useState } from 'react';
import { DollarSign, Send, Eye, Download, Plus, AlertCircle, FileText, Upload as UploadIcon, Edit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import InvoiceForm from '../forms/InvoiceForm';

const BillingInvoices = () => {
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const invoices = [
    {
      id: 'INV-001',
      client: 'Microsoft Corporation',
      service: 'Leadership Workshop',
      amount: 5000,
      status: 'Paid',
      dateIssued: '2024-02-15',
      datePaid: '2024-02-28'
    },
    {
      id: 'INV-002',
      client: 'Amazon Web Services',
      service: 'Remote Training Session',
      amount: 2500,
      status: 'Overdue',
      dateIssued: '2024-03-01',
      dateDue: '2024-03-15'
    },
    {
      id: 'INV-003',
      client: 'Google LLC',
      service: 'Innovation Workshop',
      amount: 7500,
      status: 'Pending',
      dateIssued: '2024-03-10',
      dateDue: '2024-03-25'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalPending = invoices
    .filter(inv => inv.status === 'Pending' || inv.status === 'Overdue')
    .reduce((sum, inv) => sum + inv.amount, 0);

  const handleCreateInvoice = () => {
    setShowCreateOptions(true);
  };

  const handleCreateNew = () => {
    setSelectedInvoice(null);
    setShowCreateOptions(false);
    setShowInvoiceForm(true);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceForm(true);
  };

  const handleInvoiceSubmit = (data) => {
    console.log('Invoice data:', data);
    alert(selectedInvoice ? 'Invoice updated successfully!' : 'Invoice created successfully!');
    setShowInvoiceForm(false);
    setSelectedInvoice(null);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600">${totalPending.toLocaleString()}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-green-600">$15,000</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Open Invoices</p>
                <p className="text-2xl font-bold text-blue-600">2</p>
              </div>
              <Badge className="bg-blue-500 text-white text-lg px-3 py-1">2</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Billing & Invoices</span>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCreateInvoice}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <Card key={invoice.id} className="border-2 hover:border-green-200 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{invoice.id}</h3>
                        <Badge className={getStatusColor(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </div>
                      <p className="text-blue-600 font-medium mb-1">{invoice.client}</p>
                      <p className="text-gray-600 mb-2">{invoice.service}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>Issued: {invoice.dateIssued}</span>
                        {invoice.datePaid && (
                          <>
                            <span>•</span>
                            <span>Paid: {invoice.datePaid}</span>
                          </>
                        )}
                        {invoice.dateDue && !invoice.datePaid && (
                          <>
                            <span>•</span>
                            <span className={invoice.status === 'Overdue' ? 'text-red-600 font-medium' : ''}>
                              Due: {invoice.dateDue}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          ${invoice.amount.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditInvoice(invoice)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline">
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        {invoice.status !== 'Paid' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                            <Send className="w-4 h-4 mr-1" />
                            Send Reminder
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Invoice Options Modal */}
      <Dialog open={showCreateOptions} onOpenChange={setShowCreateOptions}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Choose How to Create Your Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-6 space-y-4">
            {/* Option 1: Create New Invoice */}
            <button
              onClick={handleCreateNew}
              className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Create Detailed Invoice
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Create a comprehensive invoice with all details including seller/buyer information, line items, tax calculations (CGST/SGST/IGST), bank details, and terms & conditions. Professional format ready to send to clients.
                  </p>
                  <div className="flex items-center text-sm text-green-600 font-medium">
                    <span>Recommended - Full featured</span>
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>

            {/* Option 2: Upload Invoice */}
            <button
              onClick={() => {
                setShowCreateOptions(false);
                alert('Upload invoice functionality - Import existing invoice documents');
              }}
              className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <UploadIcon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Import Existing Invoice
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Already have an invoice prepared externally? Upload your existing PDF or Excel invoice document to track it in the system for records and client communication.
                  </p>
                  <div className="flex items-center text-sm text-blue-600 font-medium">
                    <span>Quick import option</span>
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-6 flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowCreateOptions(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Form Modal */}
      {showInvoiceForm && (
        <InvoiceForm
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceForm(false);
            setSelectedInvoice(null);
          }}
          onSubmit={handleInvoiceSubmit}
        />
      )}
    </div>
  );
};

export default BillingInvoices;